import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Stack } from "@mui/system";
import { Skeleton } from "@mui/material";
import NoPlayerFilter from "./NoPlayerFilter";
import NoPlayerItem from "./NoPlayerItem";
import {
  NO_PLAYER_SORT_OPTIONS,
  DEFAULT_NO_PLAYER_SORT,
  sortAnomaliesRows,
} from "../../Common/anomaliesSort";
import { exportRowsToXlsx } from "../../../../../../../DashboardDesktop/Anomalies/AnomaliesTabs/Utils/anomaliesColumns";
import { PLAYERS_VS_DAILY_FTD_COLUMNS } from "../../../../../../../DashboardDesktop/Anomalies/AnomaliesTabs/AnomaliesBrands/GapsBetweenPlayersPerformanceTable";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";

const NoPlayerSkeleton = () => (
  <Box sx={{ mt: 2 }}>
    <Stack spacing={2}>
      {[1, 2, 3].map((item) => (
        <Box
          key={item}
          sx={{ borderRadius: 2, border: "1px solid #e0e0e0", p: 2 }}
        >
          <Stack spacing={1.2}>
            <Skeleton width={140} height={20} />
            <Skeleton width="60%" height={18} />
          </Stack>
        </Box>
      ))}
    </Stack>
  </Box>
);

const NoPlayer = () => {
  const [searchParams] = useSearchParams();
  const workspaceParam = searchParams.get("workspace");
  const { fetchGapsBetweenPlayersData, getSiteFromWorkspace } =
    useBizDevelopmentDashboardMobile();

  const isLoading = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.loaderAnomaliesBrand,
  );
  const output = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.playersAndDailyPerformance,
  );

  useEffect(() => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;
    fetchGapsBetweenPlayersData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceParam]);

  const [brandName, setBrandName] = useState("");
  const [sortKey, setSortKey] = useState(DEFAULT_NO_PLAYER_SORT.key);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    DEFAULT_NO_PLAYER_SORT.dir,
  );

  const brandList = useMemo(() => {
    const set = new Set<string>();
    (output ?? []).forEach((row: Record<string, any>) => {
      const name = String(row?.brand_name ?? "").trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [output]);

  const filteredOutput = useMemo(() => {
    const search = brandName.trim().toLowerCase();
    if (!search) return output ?? [];
    return (output ?? []).filter((row: Record<string, any>) =>
      String(row?.brand_name ?? "")
        .toLowerCase()
        .includes(search),
    );
  }, [output, brandName]);

  const sortedOutput = useMemo(
    () => sortAnomaliesRows(filteredOutput, sortKey, sortDir),
    [filteredOutput, sortKey, sortDir],
  );

  const brandCount = filteredOutput.length;

  const handleApplySort = (key: string, dir: "asc" | "desc") => {
    setSortKey(key);
    setSortDir(dir);
  };

  const handleDownload = () => {
    exportRowsToXlsx(
      sortedOutput,
      PLAYERS_VS_DAILY_FTD_COLUMNS,
      "no-player-report.xlsx",
    );
  };

  return (
    <>
      <NoPlayerFilter
        brandCount={brandCount}
        onDownload={handleDownload}
        downloadDisabled={isLoading || brandCount === 0}
        sortKey={sortKey}
        sortDir={sortDir}
        onApplySort={handleApplySort}
        sortOptions={NO_PLAYER_SORT_OPTIONS}
        brandList={brandList}
        brandName={brandName}
        onBrandNameChange={setBrandName}
      />
      {isLoading ? (
        <NoPlayerSkeleton />
      ) : (
        <NoPlayerItem sortedData={sortedOutput} />
      )}
    </>
  );
};

export default NoPlayer;
